import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { auth } from "../../../firebaseConfig";
import { signInWithEmailAndPassword } from "firebase/auth";
import API_URLS from "../apiUrls";
import { showModal, hideModal, swapModals } from "../modalHelpers";
import { setBackendUser } from "../../redux/authSlice";

const LoginModal: React.FC = () => {
  const dispatch = useDispatch();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const loginBackend = async () => {
    const user = auth.currentUser;
    if (!user) return;

    const idToken: string = await user.getIdToken(true);

    const res = await fetch(API_URLS.AUTH.LOGIN, {
      method: "POST",
      headers: { Authorization: `Bearer ${idToken}` },
    });
    const data = await res.json().catch(() => ({} as any));

    if (!res.ok) {
      if (res.status === 404) {
        setMessage(data?.error || "Account not found. Please register.");
        swapModals("login-modal", "register-modal");
      } else {
        setMessage(data?.error || "Login failed. Please try again.");
      }
      return;
    }

    if (data?.user) {
      // Persist for page reloads and new tabs
      try {
        const userJson = JSON.stringify(data.user);
        sessionStorage.setItem("user", userJson);
        localStorage.setItem("user", userJson);
        if (data.user.id != null) {
          sessionStorage.setItem("user_id", String(data.user.id));
          localStorage.setItem("user_id", String(data.user.id));
        }
        if (data.user.name) {
          sessionStorage.setItem("user_name", data.user.name);
          localStorage.setItem("user_name", data.user.name);
        }
      } catch { /* quota errors */ }

      // Push to Redux — AuthMenu and any other subscriber re-renders immediately
      dispatch(setBackendUser(data.user));
    }

    hideModal("login-modal");
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
      await loginBackend();
    } catch (err: any) {
      const code: string = err?.code || "";
      if (code === "auth/user-not-found" || code === "auth/invalid-credential" || code === "auth/invalid-email") {
        setMessage("No account found for this email. Please register.");
        swapModals("login-modal", "register-modal");
      } else if (code === "auth/wrong-password") {
        setMessage("Incorrect password. Please try again.");
      } else {
        setMessage(err?.message || "Login failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="modal fade" id="login-modal" tabIndex={-1} aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "90vw", justifyContent: "center" }}>
          <div className="modal-content border-0 rounded-4 shadow-lg overflow-hidden" style={{ height: "90vh" }}>
            <div className="row g-0 h-100">
              {/* LEFT: Full-bleed image */}
              <div className="col-lg-6 d-none d-lg-block h-100">
                <img
                  src="/assets/img/banner/loginPage.png"
                  alt="Login Banner"
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
              </div>

              {/* RIGHT: form */}
              <div className="col-lg-6 d-flex h-100 position-relative">
                <button
                  type="button"
                  className="btn p-0 position-absolute"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  style={{ top: 16, right: 20 }}
                  onClick={() => hideModal("login-modal")}
                >
                  <i className="ti ti-x fs-20" />
                </button>
                <div className="d-flex flex-column justify-content-center align-items-center w-100 px-4 px-lg-5">
                  <h3 className="fw-semibold text-center mb-4" style={{ letterSpacing: "0.3px" }}>
                    Sign in
                  </h3>

                  <form onSubmit={handleEmailLogin} noValidate style={{ width: "100%", maxWidth: 520 }}>
                    <div className="mb-3">
                      <label className="form-label">Email</label>
                      <div className="input-group">
                        <span className="input-group-text"><i className="isax isax-message" /></span>
                        <input
                          type="email"
                          className="form-control form-control-lg"
                          placeholder="you@example.com"
                          autoComplete="username"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                        />
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Password</label>
                      <div className="input-group">
                        <span className="input-group-text"><i className="isax isax-lock" /></span>
                        <input
                          type={isPasswordVisible ? "text" : "password"}
                          className="form-control form-control-lg"
                          placeholder="••••••••"
                          autoComplete="current-password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                        />
                        <button
                          type="button"
                          className="input-group-text bg-white border-start-0"
                          onClick={() => setPasswordVisible((v) => !v)}
                          aria-label="Toggle password"
                        >
                          <i className={`isax ${isPasswordVisible ? "isax-eye" : "isax-eye-slash"}`} />
                        </button>
                      </div>
                    </div>

                    <button type="submit" className="btn btn-primary btn-lg w-100 rounded-pill" disabled={loading}>
                      {loading ? "Logging in..." : "Login"}
                    </button>

                    {message && <p className="text-center mt-3 text-danger">{message}</p>}

                    <p className="text-center mt-4 mb-0">
                      Not a user?{" "}
                      <Link
                        to="#"
                        className="link-primary fw-semibold"
                        onClick={(e) => { e.preventDefault(); swapModals("login-modal", "register-modal"); }}
                      >
                        Create an account
                      </Link>
                    </p>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginModal;
